/**
 * intelligence.ts — typed wire contracts + pure presentational helpers for the
 * Phibian Intelligence surfaces that EXIST in the API domain today.
 *
 * Every schema here re-declares a shape the API already serves (see
 * `apps/api/src/domains/intelligence/**`). The API package is deliberately NOT
 * imported: the admin UI must validate and render these shapes without pulling
 * server code, and the two declarations must drift loudly rather than silently
 * when the domain evolves. The source of truth for each schema is named in its
 * doc comment.
 *
 * Deliberately ABSENT (withdrawn, lead-owned): recommendation persistence and
 * approval semantics — and, since F278, recommendation RENDERING too: there is
 * no `recommendation` result kind, because the turn projector has never
 * produced one. Nothing here creates, validates, persists or displays a
 * recommendation; `recommendationId` appears only as opaque attribution on
 * evidence. The only HTTP surface here is the two live doors (`turn`, `tools`
 * at the bottom of the file); everything else is schemas, types and pure
 * functions.
 *
 * Sources of truth in the API domain:
 *   - ToolDefinition / ToolOutcome / ToolEvidence / ToolConfirmation →
 *     `domains/intelligence/tools/types.ts`
 *   - DashboardSpec language → `domains/intelligence/dashboards/spec.ts`
 *   - Metric facts → `domains/dashboard/metricCatalog.ts` +
 *     `domains/intelligence/dashboards/planner.ts` (program/site filter support)
 *   - TurnSummary → composed from `domains/intelligence/telemetry.ts`,
 *     `types.ts` (Usage/FinishReason) and `tools/toolLoop.ts` (round counts)
 */
import { z } from 'zod';
import { get, post, postEventStream } from './transport.js';
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
/** Lowercase hex sha256: exactly 64 hex chars (same shape as evidence/verify.ts). */
const SHA256_HEX = /^[0-9a-f]{64}$/i;
/**
 * A JSON Schema object as it travels on the wire (a plain JSON value). The
 * server derives these from the zod schemas a tool declares; the client only
 * ever treats them as data.
 */
export const jsonSchemaObjectSchema = z.record(z.string(), z.unknown());
/** Token accounting for one completion (domains/intelligence/types.ts `Usage`). */
export const usageSchema = z.object({
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    /** Input tokens the provider served from its prompt cache, when reported. */
    cachedInputTokens: z.number().int().min(0).optional(),
});
// ---------------------------------------------------------------------------
// Tool advertisement (the "what tools can the model/UI see" surface)
// ---------------------------------------------------------------------------
/**
 * Action-risk classes (tools/types.ts `ToolRiskClass`). READ tools retrieve;
 * DRAFT tools propose; CONFIRM_REQUIRED tools execute a change only after a
 * human confirmed it; PROHIBITED_AUTONOMOUS actions must never run through the
 * model loop at all.
 */
export const toolRiskClassSchema = z.enum([
    'READ',
    'DRAFT',
    'CONFIRM_REQUIRED',
    'PROHIBITED_AUTONOMOUS',
]);
/** Scope types a tool permission may be held on ('org' means org-wide). */
export const toolPermissionScopeTypeSchema = z.enum(['org', 'program', 'location']);
/**
 * A tool as advertised to clients (the serializable projection of a
 * `ToolDefinition`). Input/output schemas are JSON Schema objects — the zod
 * declarations themselves are server-only. Permission groups keep the domain's
 * semantics: EVERY group must be satisfied (AND across groups), a group is
 * satisfied by holding ANY of its keys (OR within a group).
 */
export const toolAdvertisementSchema = z.object({
    /** Stable id. Never renamed server-side. */
    id: z.string().min(1),
    /** Tool family (workforce, schedules, dashboards, …). */
    family: z.string().min(1),
    /** One-line description for model tool-selection. */
    description: z.string().min(1),
    riskClass: toolRiskClassSchema,
    /** Permission groups (AND of OR-groups) checked by the executor, never the model. */
    permissionGroups: z.array(z.array(z.string().min(1))),
    permissionScopeType: toolPermissionScopeTypeSchema,
    /** Input contract as a JSON Schema object. */
    inputSchema: jsonSchemaObjectSchema,
    /** Output contract as a JSON Schema object. */
    outputSchema: jsonSchemaObjectSchema,
});
// ---------------------------------------------------------------------------
// Tool outcomes (success + typed failure kinds)
// ---------------------------------------------------------------------------
/** Stable failure taxonomy for tool execution (tools/types.ts `ToolFailure.kind`). */
export const toolFailureKindSchema = z.enum([
    'denied',
    'invalid_input',
    'not_found',
    'unsupported',
    'internal',
]);
// ---------------------------------------------------------------------------
// Tool evidence manifest (handoff interface 3)
// ---------------------------------------------------------------------------
/** One accessed-resource entry: a resource TYPE + row count, never rows. */
export const evidenceAccessedEntrySchema = z.object({
    resourceType: z.string().min(1),
    count: z.number().int().min(0),
});
/** The org/program/site scoping a retrieval ran under. */
export const evidenceScopeSchema = z.object({
    orgId: z.string().uuid(),
    programIds: z.array(z.string().uuid()).optional(),
    siteIds: z.array(z.string().uuid()).optional(),
});
/**
 * Structured evidence attached to every tool result (tools/types.ts
 * `ToolEvidence`). Emitted for failures too: a failed retrieval reports what
 * was attempted. `params` is the schema-decoded tool input actually executed
 * (opaque to the envelope); `contentHash` is a sha256 over the canonical form
 * of the returned content so the manifest can be verified without storing
 * data; `recommendationId` is opaque attribution to a lead-owned
 * recommendation — it grants nothing.
 */
export const toolEvidenceSchema = z.object({
    /**
     * The SERVER-ISSUED reference for this entry (`ev_` + 32 hex) — the only
     * string a model may cite, and the only one the grounding audit resolves.
     * Minted by the tool executor from a cryptographic random source, never
     * derived from the tool id or the entry's position, so it cannot be
     * computed by anything that did not receive it.
     *
     * Optional on the wire because failure envelopes from older servers may
     * predate issuance; an entry without one is simply not citable.
     */
    evidenceId: z
        .string()
        .regex(/^ev_[0-9a-f]{32}$/)
        .optional(),
    toolId: z.string().min(1),
    scope: evidenceScopeSchema,
    accessed: z.array(evidenceAccessedEntrySchema),
    /** Server clock at retrieval time. */
    atMs: z.number().finite().min(0),
    params: z.unknown().optional(),
    contentHash: z.string().regex(SHA256_HEX).optional(),
    recommendationId: z.string().uuid().optional(),
});
/** The success arm of a tool outcome: a value plus its evidence manifest. */
export const toolSuccessSchema = z.object({
    ok: z.literal(true),
    value: z.unknown(),
    evidence: toolEvidenceSchema,
});
/** The failure arm: a stable, caller-safe reason + kind; evidence may be present. */
export const toolFailureSchema = z.object({
    ok: z.literal(false),
    /** Stable, caller-safe reason. Never a raw stack or SQL. */
    reason: z.string().min(1),
    kind: toolFailureKindSchema,
    evidence: toolEvidenceSchema.optional(),
});
/** Discriminated on `ok` — mirrors the domain's `ToolOutcome<T>` union. */
export const toolOutcomeSchema = z.discriminatedUnion('ok', [toolSuccessSchema, toolFailureSchema]);
// ---------------------------------------------------------------------------
// Tool confirmation (confirm-before-execute handoff constraint 4)
// ---------------------------------------------------------------------------
/**
 * A human confirmation naming exactly what will change (tools/types.ts
 * `ToolConfirmation`). Binds to a server-issued plan via `proposalId` and
 * names the exact diff via `planDigest`.
 */
export const toolConfirmationSchema = z.object({
    /** Server-issued plan id this confirmation approves (binds to plan.planId). */
    proposalId: z.string().uuid(),
    /** Server-computed digest of the exact diff being confirmed (plan.diffDigest). */
    planDigest: z.string().regex(SHA256_HEX),
    /** Human-readable summary of the change, shown to the user. */
    summary: z.string().min(1),
    /** Server clock at issuance. */
    issuedAtMs: z.number().int().min(0),
    /** Server clock at expiry (TTL-bounded). */
    expiresAtMs: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Dashboard spec (dashboards/spec.ts — re-declared field-for-field)
// ---------------------------------------------------------------------------
/** Current dashboard spec language version. Bump only with a migration rule. */
export const DASHBOARD_SPEC_VERSION = 1;
export const dashboardSpecVersionSchema = z.literal(1);
/** Period comparison: show the prior period alongside the current one. */
export const dashboardCompareSchema = z.boolean().default(false);
/** Scoping filters. Only dimensions the data layer actually supports appear here. */
export const dashboardSpecFiltersSchema = z
    .object({
    /** Restrict to these programs (metrics that consume program scoping). */
    programIds: z.array(z.string().uuid()).max(50).optional(),
    /** Restrict to these sites (site-dimension metrics only). */
    siteIds: z.array(z.string().uuid()).max(50).optional(),
    /** Custom date range; used when periodPreset is 'custom'. */
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
})
    // `.strict()` to match the persisted contract (`dashboardLayoutFiltersSchema`,
    // dashboard/layoutSchemas.ts): specs persist 1:1 into `dashboard_layout.filters`,
    // and a stripped unknown key would be a silently-dropped filter — the model
    // (and the caller reading the plan back) must be told no, not have the key
    // vanish between plan and row (F174).
    .strict()
    .default({});
/**
 * The breakdown vocabulary — `DASHBOARD_WIDGET_GROUPINGS` in `dashboards/spec.ts`,
 * which is itself `ANALYTICS_GROUPINGS` less `none`.
 *
 * Restated here because this package cannot import the server, and pinned by
 * `intelligence-spec-parity.test.ts` (F301) so the restatement cannot drift into
 * a different vocabulary than the one the planner refuses by.
 */
export const dashboardWidgetGroupBySchema = z.enum(['program', 'site', 'day', 'week']);
export const dashboardWidgetSpecSchema = z
    .object({
    metricKey: z.string().min(1).max(96),
    visualization: z.enum(['kpi', 'line', 'bar', 'table']),
    width: z.enum(['full', 'half', 'third']).default('full'),
    /** Optional threshold; only valid when the metric supports one. */
    threshold: z.number().finite().optional(),
    /**
     * Split this measure into a series — one row per program, site, day or week
     * (F286). Which (metric, groupBy) pairs are legal is the SERVER's decision,
     * made against the same object the analytics executor refuses by; this
     * schema admits the vocabulary and nothing more.
     */
    groupBy: dashboardWidgetGroupBySchema.optional(),
})
    // F301 × F296. STRICT, TO MATCH THE SERVER — and it did not, which is the
    // finding. `dashboards/spec.ts` closed every object in the language so that a
    // key nobody admits is refused rather than stripped; this hand-copied mirror
    // stayed permissive, so a payload the server would have refused decoded
    // cleanly here with the offending key silently gone. A mirror that is more
    // forgiving than the thing it mirrors is not a mirror, it is a second, wrong
    // contract — and it was already one field ahead of this lane's own change.
    .strict();
export const dashboardSpecSchema = z
    .object({
    specVersion: dashboardSpecVersionSchema,
    name: z.string().trim().min(1).max(120),
    scope: z.enum(['org', 'personal']).default('org'),
    periodPreset: z
        .enum([
        'this_week',
        'last_week',
        'this_month',
        'last_month',
        'last_30_days',
        'ytd',
        'all',
        'custom',
    ])
        .default('this_week'),
    compare: dashboardCompareSchema,
    filters: dashboardSpecFiltersSchema,
    widgets: z.array(dashboardWidgetSpecSchema).min(1).max(50),
})
    // F301, the level above: see `dashboardWidgetSpecSchema`. The server's spec
    // object is `.strict()` and this one was not.
    .strict();
// ---------------------------------------------------------------------------
// Metric info (the metric catalog projection the dashboard UI renders)
// ---------------------------------------------------------------------------
/** Metric families (dashboard/metricCatalog.ts `MetricFamily`). */
export const metricFamilySchema = z.enum([
    'workforce',
    'scheduling',
    'field_operations',
    'documentation',
    'exceptions',
    'service_client',
]);
/** Metric value unit (dashboard/metricCatalog.ts `MetricUnit`). */
export const metricUnitSchema = z.enum(['count', 'hours', 'percent']);
/** Widget visualization kinds (spec.ts widget language). */
export const widgetVisualizationSchema = z.enum(['kpi', 'line', 'bar', 'table']);
/**
 * One metric as the `dashboard.metrics` TOOL describes it, projected from
 * `MetricDefinition` + the planner's dimension support tables
 * (`metricSupportsSiteFilter` / `PROGRAM_FILTER_METRICS`). Threshold config
 * detail (range, direction, default) is deliberately not carried — the editor
 * asks the server when it needs it; this shape answers "which capabilities
 * does this metric have".
 *
 * NO CLIENT CONSUMES IT TODAY, AND THAT IS NOT AN OVERSIGHT (F275). It mirrors
 * a TOOL output — `dashboardTools.ts` builds this for the model — not an HTTP
 * response, so no door serves it to a browser: `GET /api/dashboard/metrics`
 * serves `MetricDefinition`, which carries no `supportsSiteFilter` /
 * `supportsProgramFilter`. `DashboardPreviewCard` used to take an optional
 * array of these that no host ever filled; that prop is gone. The declaration
 * stays because it is the drift detector this module's header exists for, and
 * `dashboardTools.ts` is under active change — deleting the mirror while the
 * mirrored shape moves is the one moment not to.
 */
export const metricInfoSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    family: metricFamilySchema,
    /** Legal visualizations for the metric (≥1). */
    visualizations: z.array(widgetVisualizationSchema).min(1),
    unit: metricUnitSchema,
    supportsThreshold: z.boolean(),
    supportsSiteFilter: z.boolean(),
    supportsProgramFilter: z.boolean(),
});
// ---------------------------------------------------------------------------
// Turn summary (one model turn as a UI event)
// ---------------------------------------------------------------------------
/**
 * THE CLOSED CUSTOMER-FACING REFUSAL VOCABULARY, MIRRORED (F409).
 *
 * The authority is `apps/api/src/domains/audit/refusalCategory.ts`
 * (`REFUSAL_CATEGORIES`), which owns the closure because it owns the surface
 * that renders it. This is a literal mirror rather than an import for the same
 * reason every other mirror in this package is one: `@cwos/api-client` is
 * consumed by the browser bundle and must not pull in an API-domain module.
 * The mirror is CROSS-CHECKED, not trusted — `intelligence-refusal-category-
 * crosses-the-wire.test.ts` asserts these members equal `REFUSAL_CATEGORIES`
 * exactly, in both directions, so a sixth member added there without a decision
 * here fails a test that names it.
 *
 * WHAT EACH MEANS TO A READER is documented once, in the audit module. What
 * matters HERE is only that `small_group_protected` is not `no_access` and not
 * `not_offered`: a privacy floor working correctly, a permission gap, and a
 * capability the product does not have need three different sentences, and
 * before this they got one.
 */
export const refusalCategorySchema = z.enum([
    'no_access',
    'needs_confirmation',
    'small_group_protected',
    'not_offered',
    'request_unclear',
]);
/** One tool invocation within a turn, reduced to telemetry-safe fields. */
export const turnToolOutcomeSchema = z.object({
    toolId: z.string().min(1),
    outcome: z.enum(['ok', 'denied', 'invalid_input', 'not_found', 'unsupported', 'internal']),
    /**
     * WHICH KIND OF REFUSAL, when there was one (F409).
     *
     * WHY THIS FIELD EXISTS. `outcome: 'unsupported'` is what the analytics
     * executor raises BOTH for a measure the product does not offer AND for a
     * figure it will not publish because too few people stand behind it
     * (`withheld()`, code `subjects_below_threshold`). The server has always
     * drawn the distinction — `tools/refusalCategory.ts` files that code as
     * `small_group_protected` and pointedly NOT as `no_access`, so that nobody is
     * sent to an admin who has nothing to grant — and the distinction died at
     * this boundary. A privacy floor doing its job and a capability gap arrived
     * identically, and the admin surface said so in a comment because it could
     * not say anything else.
     *
     * IT LEAKS NO COHORT SIZE, WHICH IS THE WHOLE DIFFICULTY OF THE ITEM. Three
     * separate reasons, and the third is the load-bearing one:
     *
     *   1. The value is a member of a five-word closed vocabulary. There is no
     *      number in it, no metric, no person, no permission key, no free text.
     *      `small_group_protected` says a floor applied. It does not say how many
     *      people are behind the figure, how far below the floor they are, or
     *      even that the count is non-zero.
     *   2. It does not change WHICH requests are refused — only the label on a
     *      refusal that was already fully observable as `outcome: 'unsupported'`.
     *      A differencing attack that narrows a window until the refusal flips
     *      gets exactly the same flip point it got before this field existed, so
     *      no new oracle is opened. (That attack is real, still open, and
     *      documented where it lives: `analytics/engineExecutor.ts` and
     *      `dashboard/cohortClosure.ts`.)
     *   3. The SAME response already says more than this in words. The refusal
     *      sentence the turn carries is "too few people stand behind X here for
     *      that figure to be reported without identifying them (fewer than N
     *      stand behind it)" — where N is `MIN_COHORT_SIZE`, a published
     *      constant. The engine's own rule, written at that call site: THE FLOOR
     *      IS A PUBLISHED CONSTANT; THE COHORT IS THE SECRET. This field restates
     *      the published half as a machine token so a client can branch on it
     *      without parsing prose. It carries nothing across that the prose beside
     *      it did not already carry.
     *
     * Absent when the call succeeded, and absent for an internal fault: nothing
     * was decided and nothing was withheld there, so there is no category and
     * absence is the honest answer rather than a sixth member meaning "broke".
     */
    refusalCategory: refusalCategorySchema.optional(),
});
/**
 * `errorKind` WAS HERE AND CARRIED NOTHING (F409).
 *
 * It was declared "normalized error kind; absent when outcome is 'ok'", and its
 * only producer — `tools/executor.ts` — filled it with `outcome`, the field
 * directly above it, unchanged. Every consumer that read it learned exactly
 * what `outcome` had already told them, and its NAME ("the kind of error")
 * promised the one thing this object could not say, which is why the admin
 * surface's `answerLimits` documented the missing distinction in a comment
 * instead of rendering it.
 *
 * Removed rather than made honest, on F297's precedent two fields down: a
 * duplicate that advertises information it does not have is the same class of
 * defect as a dead enum member, and the fix is the same — stop publishing it.
 * What replaces it is `refusalCategory`, which is not a copy of anything.
 *
 * WHAT THIS COSTS, stated for the same reason F297 stated it: this package is
 * `private: true` and ships in the same deploy as the route, and `outcome`
 * still carries everything `errorKind` ever did. The tool-call TELEMETRY event
 * keeps its `errorKind` — that surface has field parity with attempt-based
 * model telemetry, where the two genuinely differ.
 */
/**
 * A UI-facing summary event for one model turn (assistant answer + its tool
 * loop). Counts and ids only — never message content or tool arguments.
 * Composed from the surfaces that exist today: `CompletionResult` (finish
 * reason, usage, model/provider), `ToolLoopResult` (rounds) and tool-call
 * telemetry (per-call outcomes, cost).
 */
export const turnSummarySchema = z.object({
    kind: z.literal('intelligence.turn_summary'),
    turnId: z.string().uuid(),
    atMs: z.number().finite().min(0),
    model: z.string().min(1),
    providerId: z.string().min(1),
    finishReason: z.enum(['stop', 'tool_calls', 'length', 'content_filter']),
    usage: usageSchema,
    /** Jev context selection outcome — counts and closed tokens, never content. */
    context: z
        .object({
        decision: z.enum(['full', 'narrowed']),
        reason: z.enum([
            'no_optional_intent',
            'intent_matched',
            'session_carryover',
            'routed_intent',
        ]),
        families: z.array(z.string()).max(32),
        withheld: z.array(z.string()).max(32),
        toolsAdvertised: z.number().int().min(0),
        compactedToolResults: z.number().int().min(0),
        evidenceCount: z.number().int().min(0),
        budgetPreempted: z.boolean(),
    })
        .optional(),
    /** Decision Router outcome for the turn — closed tokens and numbers only. */
    routing: z
        .object({
        tier: z.enum(['fast', 'strong', 'out_of_scope']),
        modelId: z.string().max(200).optional(),
        familyIntent: z.array(z.string()).max(32),
        decisions: z
            .array(z.object({
            policyId: z.string().max(200),
            policyVersion: z.string().max(20),
            route: z.enum(['deterministic', 'jev', 'reasoning', 'human']),
            reason: z.string().max(100),
            value: z.string().max(100).optional(),
            jevConfidence: z.number().min(0).max(1).optional(),
            fallbackReason: z.string().max(100).optional(),
            latencyMs: z.number().min(0),
            costUsd: z.number().min(0),
        }))
            .max(16),
    })
        .optional(),
    /** USD cost of the whole turn, when provider pricing is available. */
    costUsd: z.number().finite().min(0).optional(),
    /** How many tool rounds ran (0 = the model called no tools). */
    toolRounds: z.number().int().min(0),
    toolOutcomes: z.array(turnToolOutcomeSchema).max(50),
    /**
     * How the turn ENDED, and only in ways a 200 can honestly report.
     *
     * 'completed' = the loop finished; 'round_limit' = a budget dimension
     * (rounds, tokens, cost, latency) halted it with work left, so the answer on
     * screen may be built on less than the question needed.
     *
     * THERE IS NO 'error' MEMBER, AND ITS REMOVAL IS THE POINT (F297). One was
     * published here for the whole life of this schema, alongside an `errorKind`
     * that existed to explain it, and neither could ever be emitted: the only
     * producer of this object is the turn route's final `reply.send`, and a turn
     * that fails answers 502 / 503 / 409 with an error body and NO summary at all
     * — it returns before the summary is built. So every client that parsed this
     * schema was told a turn could report itself as failed inside a success
     * response, and wrote handling for a case the server cannot produce. The
     * admin surface's `turnEndingNote` carried exactly such a branch: a sentence
     * a person could never be shown, that no test could reach, and that read as
     * covered because the type said the case existed.
     *
     * The other way to resolve it — making the member reachable — is worse and
     * was rejected deliberately: it would mean answering 200 with a summary for a
     * turn that failed, hiding a failure behind a success code and breaking every
     * client's error handling. A dead enum member traded for a lying one.
     *
     * WHAT THIS COSTS. The enum is now narrower than the server could ever grow
     * into without a matching client, because `z.enum` REJECTS an unknown member
     * rather than passing it through. That is acceptable here and would not be in
     * a published SDK: this package is `private: true`, consumed only by apps in
     * this repo, and ships in the same deploy as the route. Adding a member later
     * is a two-line change in one commit — and, unlike today, it will be a
     * deliberate one, because the surface that has to say something about the new
     * ending will fail to typecheck until somebody decides what it says.
     */
    status: z.enum(['completed', 'round_limit']),
});
// ---------------------------------------------------------------------------
// Pure presentational helpers
// ---------------------------------------------------------------------------
/** Human labels for tool outcome kinds. Deterministic, locale-independent. */
export const OUTCOME_KIND_LABELS = {
    ok: 'Completed',
    denied: 'Denied',
    invalid_input: 'Invalid input',
    not_found: 'Not found',
    unsupported: 'Unsupported',
    internal: 'Internal error',
};
/** Human-readable label for a tool outcome kind. */
export function formatOutcomeKind(kind) {
    return OUTCOME_KIND_LABELS[kind];
}
/** First `chars` hex chars of a hash, lowercased, with an ellipsis when truncated. */
export function shortenHash(hash, chars) {
    const head = hash.slice(0, chars).toLowerCase();
    return hash.length > chars ? `${head}…` : head;
}
/** Naive pluralization for evidence resource types ("worker" → "workers"). */
function pluralizeResourceType(resourceType, count) {
    return count === 1 ? resourceType : `${resourceType}s`;
}
/**
 * One-line summary of an evidence manifest: accessed counts per resource type
 * plus a shortened content hash, e.g. `"3 workers · 2 service_events · hash 1a2b…"`.
 * Deterministic — no dates, no locale formatting.
 */
export function formatEvidenceSummary(evidence) {
    const parts = [];
    for (const entry of evidence.accessed) {
        parts.push(`${entry.count} ${pluralizeResourceType(entry.resourceType, entry.count)}`);
    }
    if (parts.length === 0)
        parts.push('nothing accessed');
    if (evidence.contentHash !== undefined) {
        parts.push(`hash ${shortenHash(evidence.contentHash, 4)}`);
    }
    return parts.join(' · ');
}
/**
 * Deterministic USD formatting for turn/tool costs. Four decimal places below
 * $1 (token costs are fractions of a cent), two above. Undefined (a provider
 * without pricing) and non-finite values render as an em dash — never a
 * fabricated zero.
 */
export function formatCostUsd(costUsd) {
    if (costUsd === undefined || !Number.isFinite(costUsd))
        return '—';
    const absolute = Math.abs(costUsd);
    const digits = absolute < 1 ? 4 : 2;
    const sign = costUsd < 0 ? '-$' : '$';
    return `${sign}${absolute.toFixed(digits)}`;
}
/**
 * The text a UI shows when asking a human to confirm a change: the summary
 * verbatim, plus the plan digest NAMING the exact diff the confirmation
 * applies. Deterministic; the UI adds the yes/no affordances.
 */
export function formatConfirmationPrompt(summary, planDigest) {
    return [
        'Confirm this change before it runs.',
        '',
        summary,
        '',
        `Plan ${shortenHash(planDigest, 8)} — confirming applies exactly the diff this plan names.`,
    ].join('\n');
}
/**
 * True when a tool's risk class involves the human-confirmation gate:
 * CONFIRM_REQUIRED executes only after a confirmation naming the diff, and
 * PROHIBITED_AUTONOMOUS must never run at all (the executor refuses it). READ
 * and DRAFT tools never need a confirmation prompt.
 */
export function hasConfirmationRisk(riskClass) {
    return riskClass === 'CONFIRM_REQUIRED' || riskClass === 'PROHIBITED_AUTONOMOUS';
}
// ---------------------------------------------------------------------------
// Rich operational result payloads (Intelligence makeover, lane C)
// ---------------------------------------------------------------------------
//
// An answer is not only prose. These are the TYPED objects the surface renders
// beside it — a KPI, a table, a chart, record cards, an automation, a proposed
// dashboard. Nothing here is parsed out of the answer text and nothing is
// inferred from wording: a component renders only what a typed payload
// carries, and renders NOTHING when the payload is absent.
//
// PROVENANCE OF EVERY FIELD NAME BELOW. Each schema names the server contract
// it mirrors in its doc comment. Where a server payload does not exist yet the
// comment says so in those words — the shape is still the server's (copied
// from the domain contract that owns the concept), never an invention of the
// client's, so when the payload lands the two declarations either agree or
// drift loudly.
//
// THE ENVELOPE — the `kind` discriminator and `TurnResponse.results` — was this
// lane's own proposal, and the server has since ADOPTED it: the turn route
// projects tool outcomes into `results` through
// `intelligence/results/project.ts`, whose `./types.ts` is now the authority
// for every shape below (F221a). `results` stays `.optional()` on purpose: a
// turn whose tools produced no typed object omits the field entirely, and a
// prose-only answer's response is byte-identical to what the route served
// before the field existed.
/**
 * How far a payload's numbers were disclosed to THIS caller.
 *
 * Not a new idea and not a new shape: this is the dashboard domain's disclosure
 * envelope (`apps/api/src/domains/dashboard/analyticsSchemas.ts`, re-declared as
 * interfaces in `dashboard.ts` here), expressed as zod so an Intelligence result
 * can be validated at the boundary. `level: 'aggregate'` is the de-identified
 * variant; `suppression.cells` names the fields set to `null` because fewer than
 * `minCohortSize` people stand behind them.
 *
 * The whole reason it rides along: a suppressed measure arrives as `null`, and
 * `null` rendered as `0` is the product's worst defect — "nobody worked" said
 * about a figure that was withheld. A renderer that holds this envelope can say
 * which of the two a `null` is.
 *
 * SERVED (F222). `analyticsQueryOutput` carries `disclosure` and the turn route
 * projects it onto the KPI and the chart. It stays `.optional()` because it is
 * an analytics-side envelope: the roster and supervision tables carry no
 * suppression rule of their own, and a required field there would have to be
 * filled with a claim about disclosure that nothing computed.
 *
 * WHAT `suppression.cells[].fields` NAMES on an Intelligence result: the `key`
 * of each {@link AnalyticsGroupedRow} whose `value` was blanked for small-N. A
 * HEADLINE figure under the floor never arrives at all — the executor refuses
 * it as `subjects_below_threshold`, the tool fails, and rule "a refused tool
 * projects no result" means there is no KPI to misread. So on this contract
 * `value: null` is always honest absence, and a withheld figure is always a
 * named row in a breakdown.
 */
export const analyticsSuppressedCellSchema = z
    .object({
    /** The field names set to `null` in this response. */
    fields: z.array(z.string().min(1)),
    /**
     * OPTIONAL, AND THE SERVER NO LONGER SENDS IT (F359).
     *
     * This was "distinct subjects behind the scarcest measure in the block",
     * and it was an exact headcount published to exactly one audience: the
     * de-identified caller it is a secret from. An identified caller returns
     * early from every producer of a suppression cell and never reaches this
     * envelope at all, so the count had no audience entitled to it.
     *
     * The window is the caller's, to the day, so the refusals could be walked
     * one day at a time and read as a per-day census with nothing subtracted.
     *
     * WHY OPTIONAL RATHER THAN GONE: this schema is `.strict()` and was
     * `z.number().int().min(0)`, so a server that simply omitted the field
     * would make this parse FAIL — breaking every caller harder than the leak
     * ever did. Optionality is what let the server and client change together.
     * A cell WITHOUT this field is a withheld figure; absence here has never
     * meant "nothing was suppressed", and no renderer may read it that way.
     * Nothing in this package populates or displays it. Do not reintroduce it.
     */
    cohortSize: z.number().int().min(0).optional(),
})
    .strict();
export const analyticsSuppressionSchema = z
    .object({
    minCohortSize: z.number().int().min(1),
    cells: z.array(analyticsSuppressedCellSchema),
})
    .strict();
export const resultDisclosureSchema = z
    .object({
    level: z.enum(['identified', 'aggregate']),
    /** Top-level fields omitted from this response entirely. */
    withheld: z.array(z.string().min(1)),
    suppression: analyticsSuppressionSchema.optional(),
})
    .strict();
/**
 * True when `field` was suppressed for small-N — i.e. its `null` means WITHHELD,
 * not "no data was recorded".
 *
 * The two are different sentences and the difference is not recoverable from the
 * value, which is `null` either way. Every renderer of a nullable measure asks
 * this before it decides what to print.
 */
export function isSuppressedField(disclosure, field) {
    if (disclosure?.suppression === undefined)
        return false;
    return disclosure.suppression.cells.some((cell) => cell.fields.includes(field));
}
/*
 * `suppressedCohortSize(disclosure, field)` STOOD HERE AND IS DELETED (F359).
 *
 * It existed to hand a caller the exact headcount behind a suppressed field,
 * on the argument that a UI needs it to say WHY a figure is blank rather than
 * render a misleading zero. The first half of that argument survives; the
 * second half is served by `isSuppressedField` above, which distinguishes
 * WITHHELD from ABSENT — the distinction the renderers actually needed — and
 * by `minCohortSize`, which names the published floor.
 *
 * It is deleted rather than left returning null because an exported accessor
 * whose entire purpose is to extract the field the server just stopped sending
 * is an invitation to start sending it again. It had no callers.
 */
/**
 * The four evidence classes the makeover directive names (invariant 6:
 * "evidence is classed — operational / policy / uploaded / catalog").
 *
 * AWAITING SERVER SUPPORT: the runtime today classes analytics evidence three
 * ways (`AnalyticsEvidence`: catalog / operational / supporting — mirrored
 * verbatim below) and carries no four-class marker on any other payload. This
 * enum is the directive's vocabulary, declared so the non-analytics blocks can
 * class their evidence the moment the server does.
 */
export const resultEvidenceClassSchema = z.enum(['operational', 'policy', 'uploaded', 'catalog']);
/** One classed evidence line on a non-analytics result block. */
export const resultEvidenceItemSchema = z
    .object({
    class: resultEvidenceClassSchema,
    /** What was consulted, in plain words. Never a row, never PHI. */
    label: z.string().min(1),
    /** How many records stood behind it, when the server counted them. */
    count: z.number().int().min(0).optional(),
})
    .strict();
// --- Records ----------------------------------------------------------------
/**
 * The record types Intelligence can point at. Each one has a surface in this
 * app; `recordHref` (admin side) decides whether that surface addresses the
 * INDIVIDUAL record or only the list it lives on, and labels the link honestly
 * either way.
 */
export const resultRecordTypeSchema = z.enum([
    'worker',
    'program',
    'site',
    'shift',
    'exception',
    'automation',
    'dashboard',
]);
/** Pill tones available to a result payload (design-tokens `Severity`). */
export const resultToneSchema = z.enum(['good', 'look', 'urgent', 'unsure', 'plain', 'sky']);
/**
 * A pointer to one real record.
 *
 * `id` is OPAQUE: it addresses the record and is never rendered. `label` is the
 * user-facing identity and is required — a payload that carries an id but no
 * label cannot be rendered as a record at all, which is the schema's way of
 * refusing to print a uuid at a person. Field names follow the shapes the
 * runtime already serves (`workforce.list_workers` → `{ id, displayName,
 * roleTitle, employmentStatus }`; `supervision.status` → `{ workerId,
 * displayName, supervisorName, statusLabel, statusMeaning }`), projected onto
 * one label/sublabel/facts shape so one card renders any of them.
 */
export const resultRecordSchema = z
    .object({
    type: resultRecordTypeSchema,
    /** Opaque addressing id. Never rendered. */
    id: z.string().min(1),
    /** The record's user-facing identity. Required, on purpose. */
    label: z.string().min(1),
    /** One line under the identity: role, program, status meaning. */
    sublabel: z.string().min(1).optional(),
    /** Short status words, e.g. "Overdue". Tone drives the pill only. */
    badges: z
        .array(z.object({ label: z.string().min(1), tone: resultToneSchema }).strict())
        .max(4)
        .optional(),
    /** Label/value pairs shown as a small definition list. */
    facts: z
        .array(z.object({ label: z.string().min(1), value: z.string().min(1) }).strict())
        .max(6)
        .optional(),
})
    .strict();
// --- Table ------------------------------------------------------------------
/**
 * One cell, as a closed union rather than a string.
 *
 * A string cell cannot distinguish a zero from a withheld figure from a value
 * the server never computed, and it cannot stop a uuid reaching the screen. The
 * union does all three: numbers carry their own precision, a record cell carries
 * a {@link ResultRecord} (label rendered, id used only for the link), and
 * `withheld` / `absent` are their own arms with no number to misread.
 */
export const resultCellSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), text: z.string() }).strict(),
    z
        .object({
        kind: z.literal('number'),
        value: z.number().finite(),
        precision: z.number().int().min(0).max(4).default(0),
        unit: metricUnitSchema.optional(),
    })
        .strict(),
    z.object({ kind: z.literal('record'), record: resultRecordSchema }).strict(),
    z
        .object({
        kind: z.literal('status'),
        label: z.string().min(1),
        tone: resultToneSchema,
    })
        .strict(),
    /** Withheld from THIS caller. Never a zero, never an em dash. */
    z
        .object({
        kind: z.literal('withheld'),
        /** Distinct subjects behind the suppressed figure, when the server says. */
        cohortSize: z.number().int().min(0).optional(),
    })
        .strict(),
    /** The server answered and had no value here. Different from withheld. */
    z.object({ kind: z.literal('absent') }).strict(),
]);
export const resultColumnSchema = z
    .object({
    key: z.string().min(1),
    header: z.string().min(1),
    /** Right-align and sort numerically. */
    numeric: z.boolean().default(false),
    /**
     * Sortable ONLY when the server says the column is orderable. Sorting a
     * column the payload cannot order honestly would present an arrangement as
     * a fact the server never asserted.
     */
    sortable: z.boolean().default(false),
})
    .strict();
export const resultRowSchema = z
    .object({
    /** Opaque row key. Never rendered. */
    id: z.string().min(1),
    cells: z.record(z.string(), resultCellSchema),
})
    .strict();
/**
 * A staff list, a supervision queue, missed shifts, documentation, mileage, an
 * exception list — one table shape for all of them.
 *
 * SERVED for two of them (F221a): `workforce.list_workers` projects a roster
 * table and `supervision.status` a supervision queue. The column/cell
 * vocabulary here is the client's rendering contract; the DATA in it is always
 * the server's, and the server truncates to the 500-row bound below rather than
 * risk a response that fails to parse at all.
 *
 * NOT SERVED, and deliberately: `supervision.overview` (seven counts and no
 * rows) and `connectors.health` (infrastructure state, not an operational
 * answer). A table of one row of numbers is furniture.
 */
export const resultTableSchema = z
    .object({
    kind: z.literal('table'),
    title: z.string().min(1),
    /** What the reader is looking at, when the title is not enough. */
    caption: z.string().min(1).optional(),
    columns: z.array(resultColumnSchema).min(1).max(12),
    rows: z.array(resultRowSchema).max(500),
    /**
     * Why the table is empty, in the server's words. "No rows matched" and
     * "nothing here is yours to see" are different answers and the payload is
     * the only thing that knows which.
     */
    emptyMessage: z.string().min(1).optional(),
    disclosure: resultDisclosureSchema.optional(),
    evidence: z.array(resultEvidenceItemSchema).max(12).optional(),
})
    .strict();
// --- Chart ------------------------------------------------------------------
export const resultChartPointSchema = z
    .object({
    /**
     * The point's own identity in the DISCLOSURE'S vocabulary — the dimension
     * row's key (a programId, a siteId, a bucket start date), never a display
     * string.
     *
     * F228. Without it a `null` bar was un-joinable: the chart drew the same
     * gap for "we measured nothing here" and "we measured it and are not
     * telling you", which are the two sentences this whole contract exists to
     * keep apart. The server already names the second one — the analytics
     * engine pushes `{ fields: [row.key] }` into
     * `disclosure.suppression.cells` for exactly the rows it blanked (see
     * `groupRows`/`bucketRows` in `intelligence/analytics/engineExecutor.ts`)
     * — but a point carrying only its label had no key to look itself up by.
     * This is that key. It is NOT a parallel suppression flag: the disclosure
     * stays the single authority and the point merely becomes addressable
     * within it, via `isSuppressedField`.
     *
     * OPTIONAL, and deliberately. Not every plotted series has a key space a
     * disclosure is expressed in, and a required field would make an older
     * server's payload fail to parse rather than degrade. A point WITHOUT a
     * key is not "not suppressed" — it is un-joinable, and every renderer
     * treats it exactly as it did before this field existed: a `null` there is
     * drawn as an honest absence, because absence is all the payload can
     * support. What must never happen is a suppressed figure reading as a
     * zero, and no arm of that fork does.
     */
    key: z.string().min(1).optional(),
    label: z.string().min(1),
    /** null = no value at this point. Drawn as a gap, never as zero. */
    value: z.number().finite().nullable(),
})
    .strict();
export const resultChartSeriesSchema = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    points: z.array(resultChartPointSchema).max(200),
})
    .strict();
/**
 * Trend, comparison, distribution, breakdown.
 *
 * `shape` is drawn from the dashboard spec language's visualization vocabulary
 * (`spec.ts`: kpi | line | bar | table) narrowed to the two that plot: a
 * breakdown or distribution is a bar chart of one series, a comparison is a bar
 * chart of two, a trend is a line.
 *
 * SERVED for the one series that exists (F221a): `AnalyticsQueryResult.grouped`
 * — `{ key, label, value: number | null }[]` — is exactly one bar series, so a
 * grouped analytics answer arrives as a KPI AND a `bar` chart beside it. The
 * shape is `bar` and not `line` because a breakdown across programs has no
 * ordering between its categories for a line to assert.
 *
 * There is still no GENERAL multi-series payload: nothing the runtime computes
 * today produces a trend over time as a series, so `line` remains declared and
 * unserved rather than faked from a two-point comparison.
 */
export const resultChartSchema = z
    .object({
    kind: z.literal('chart'),
    shape: z.enum(['line', 'bar']),
    title: z.string().min(1),
    unit: metricUnitSchema,
    precision: z.number().int().min(0).max(4).default(0),
    series: z.array(resultChartSeriesSchema).min(1).max(4),
    /** Axis caption, e.g. the resolved period. */
    periodLabel: z.string().min(1).optional(),
    disclosure: resultDisclosureSchema.optional(),
})
    .strict();
// --- KPI --------------------------------------------------------------------
/** The three evidence classes an analytics answer carries (`AnalyticsEvidence`). */
export const analyticsResultEvidenceSchema = z
    .object({
    catalog: z.array(z.string()),
    operational: z.array(evidenceAccessedEntrySchema),
    supporting: z.array(z.string()),
})
    .strict();
export const analyticsComparisonSchema = z
    .object({
    previousPeriod: z.object({ from: z.string(), to: z.string() }).strict(),
    previousValue: z.number().finite().nullable(),
    delta: z.number().finite().nullable(),
    deltaPercent: z.number().finite().nullable(),
})
    .strict();
export const analyticsGroupedRowSchema = z
    .object({
    key: z.string(),
    label: z.string(),
    value: z.number().finite().nullable(),
})
    .strict();
/**
 * The headline number. Mirrors `analyticsQueryOutput`
 * (`apps/api/src/domains/intelligence/tools/analyticsTools.ts`) field for field,
 * which itself mirrors `AnalyticsQueryResult`
 * (`intelligence/analytics/types.ts`) — the ASK→ANSWER contract.
 *
 * Two nullables carry the whole honesty burden and they are NOT the same fact:
 *   - `value: null` with no suppression — the metric's sources were consulted
 *     and no activity was recorded. The composer's own words.
 *   - `value: null` named in `disclosure.suppression` — a real figure, WITHHELD
 *     because too few people stand behind it. Never a zero, and never the
 *     no-data sentence either.
 *
 * `plainText` is the deterministically composed answer (`compose.ts`), carried
 * so the surface can relay it verbatim rather than re-writing the number.
 */
export const analyticsKpiSchema = z
    .object({
    kind: z.literal('kpi'),
    metric: z.string().min(1),
    label: z.string().min(1),
    unit: metricUnitSchema,
    precision: z.number().int().min(0).max(4),
    period: z.object({ from: z.string(), to: z.string() }).strict(),
    value: z.number().finite().nullable(),
    /** Distinct subjects contributing, when the executor reports it. */
    subjectCount: z.number().int().min(0).optional(),
    /**
     * Human noun for the subjects, e.g. "workers".
     *
     * SERVED. Present exactly when `subjectCount` is: the executor's companion
     * table knows which population it counted, and it is not always workers —
     * `clients_served`, `appointments`, `hours_delivered` and `care_gaps` count
     * CLIENTS. The `?? 'workers'` fallback in `subjectLine` is now a fallback
     * for an older payload, not the normal case.
     */
    subjectNoun: z.string().min(1).optional(),
    /** Human label for the resolved period, e.g. "Last week". */
    periodLabel: z.string().min(1).optional(),
    grouped: z.array(analyticsGroupedRowSchema).max(200).optional(),
    comparison: analyticsComparisonSchema.optional(),
    evidence: analyticsResultEvidenceSchema,
    plainText: z.string().optional(),
    disclosure: resultDisclosureSchema.optional(),
})
    .strict();
// --- Record cards -----------------------------------------------------------
/** A set of records the answer is about: workers, programs, sites, shifts… */
export const resultRecordsSchema = z
    .object({
    kind: z.literal('records'),
    title: z.string().min(1),
    records: z.array(resultRecordSchema).min(1).max(50),
    /** Said when the server narrowed the set, in its words. */
    note: z.string().min(1).optional(),
})
    .strict();
// --- Recommendation: DELIBERATELY NOT A RESULT KIND (F278) -------------------
//
// `recommendation` was a declared `results[].kind` here, with a full schema,
// a `RecommendationCard` to render it, and a doc comment that said "AWAITING
// SERVER SUPPORT". The wait was unbounded and the branch was unreachable: the
// projector's own union
// (`apps/api/src/domains/intelligence/results/types.ts` — `ResultKpi |
// ResultTable | ResultChart | ResultRecords | ResultAutomation |
// ResultDashboardPreview`) has never had a recommendation arm, and
// `results/project.ts` has never produced one. A client written against the
// published type was branching on a case that cannot occur.
//
// The `recommendation-record` domain does persist recommendations — and it is
// registered nowhere. It has no `routes.ts`, no `index.ts` and no entry in
// `server.ts` (docs/V1-WORKFLOW-INVENTORY.md), so nothing can serve one even
// in principle. Declaring the kind here also contradicted this module's own
// header, which withdraws recommendation semantics from this client as
// lead-owned.
//
// So the schemas are removed rather than kept as a promise. When the domain is
// registered and the projector grows the arm, the decoder will reject the new
// kind LOUDLY — `turn()` parses the response through `turnResponseSchema` —
// which is the drift this file's header asks for, and a better signal than a
// dead branch that quietly renders nothing.
// --- Automation -------------------------------------------------------------
/** Authority tiers (`intelligence/automation/store.ts` `AUTOMATION_TIERS`). */
export const automationTierSchema = z.enum(['personal', 'team', 'organization']);
/** Closed schedule trigger (`store.ts` `automationTriggerSchema`). */
export const automationTriggerSchema = z
    .object({ kind: z.literal('schedule'), expression: z.string().min(1).max(64) })
    .strict();
/**
 * Audience (`store.ts`, request form from `automation/tools.ts`). `self` carries
 * no ids — a personal automation reaches its owner structurally, not by list.
 */
export const automationAudienceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('self'), selfUserId: z.string().uuid().optional() }).strict(),
    z
        .object({ kind: z.literal('team'), subjectUserIds: z.array(z.string().uuid()).min(1).max(256) })
        .strict(),
    z
        .object({
        kind: z.literal('organization'),
        programIds: z.array(z.string().uuid()).max(64).optional(),
        siteIds: z.array(z.string().uuid()).max(64).optional(),
    })
        .strict(),
]);
/** Delivery attribution — a CLOSED union: the model can never invent a sender. */
export const automationAttributionSchema = z.discriminatedUnion('model', [
    z.object({ model: z.literal('owner') }).strict(),
    z.object({ model: z.literal('organization') }).strict(),
]);
/** The validated automation request (`automation/tools.ts` `automationRequestSchema`). */
export const automationRequestSchema = z
    .object({
    specVersion: z.literal(1),
    tier: automationTierSchema,
    ruleType: z.string().min(1).max(96),
    ruleName: z.string().min(1).max(120),
    note: z.string().max(280).optional(),
    params: z.record(z.string(), z.number().finite()),
    trigger: automationTriggerSchema,
    audience: automationAudienceSchema,
    attribution: automationAttributionSchema,
})
    .strict();
/**
 * A proposed (or created) automation, in the WHEN / IF / DO shape the builder
 * already uses — same three words, same order, so one automation reads
 * identically in Ask Phibian and in Settings → Automations.
 *
 * Mirrors `planAutomationOutput` (`intelligence/automation/tools.ts`):
 * `{ planId, diffDigest, validatedRequest, persistable, reasons }`. `severity`
 * and `explanation` are `dashboard.propose_automation`'s own output fields,
 * present only when the automation was proposed from a metric. `execution` is
 * `createAutomationOutput`'s F195 honesty field — "saved, but it cannot run
 * yet" — carried so the card can relay that instead of implying it is live.
 *
 * RECONCILED WITH THE SERVER (F221a): `diffDigest` was declared required here
 * on the assumption that one card shape came from one tool. It does not. A
 * PLANNED automation comes from `automation.plan`, which computes a digest; a
 * CREATED one comes from `createAutomationOutput`, which carries `id`,
 * `authorityTier`, `ruleType`, `ruleName`, `params`, `trigger`, `audience`,
 * `attribution`, `state`, `createdAtMs` and `execution` — and NO digest. The
 * server projection therefore omits it on a created automation rather than
 * computing a lookalike, because a digest asserts a hash of specific bytes and
 * inventing one would claim an identity nobody computed. So it is optional
 * here: absent means "this automation exists, not this plan does".
 * (`planId` stays required and stays a uuid: the created row's `id` IS
 * `plan.planId` — `automation.create` writes the record with it.)
 */
export const resultAutomationSchema = z
    .object({
    kind: z.literal('automation'),
    /** Server-issued plan identity a confirmation binds to. */
    planId: z.string().uuid(),
    /** Plan-only. See the note above: a created automation carries no digest. */
    diffDigest: z.string().regex(SHA256_HEX).optional(),
    validatedRequest: automationRequestSchema,
    persistable: z.boolean(),
    /** Deterministic, caller-safe notes from the planner. */
    reasons: z.array(z.string().min(1)),
    severity: z.enum(['info', 'warning', 'critical']).optional(),
    explanation: z.string().min(1).optional(),
    execution: z
        .object({ canExecuteNow: z.boolean(), reason: z.string().min(1) })
        .strict()
        .optional(),
})
    .strict();
// --- Dashboard preview ------------------------------------------------------
/**
 * One widget of the proposed dashboard, DRAWN.
 *
 * `render` is the ordinary result union — a KPI, a chart or a table — so the
 * preview draws through the very components that draw a real answer rather
 * than through a preview-only lookalike. It is absent exactly when
 * `unavailable` is present: a measure the engine could not answer says so, and
 * shows nothing rather than a zero.
 *
 * Not `z.lazy` over the whole union: only the three payload kinds a widget can
 * actually be are admitted, so a nested `records` or a nested preview is a
 * decode failure rather than a surprise at render time.
 */
export const resultDashboardWidgetPreviewSchema = z
    .object({
    metricKey: z.string().min(1),
    /**
     * The catalogue's own name for the measure, set by the server.
     *
     * REQUIRED, and this schema is `.strict()`, so the two halves have to move
     * together: a `label` the server sends and this schema does not declare is
     * a DECODE FAILURE of the whole result, not a field quietly ignored.
     *
     * It exists so a surface never has to choose between fetching the metric
     * catalogue and de-slugging the key. The de-slug is honest but reads wrong
     * for the keys where the caption matters most — `care_gaps` is "Clients
     * behind plan", `no_shows` is "Missed shifts", `verification_failures` is
     * "Location verification failures" — and the preview executor already had
     * the catalogue open when it built the widget.
     */
    label: z.string().min(1),
    width: z.enum(['full', 'half', 'third']),
    visualization: widgetVisualizationSchema,
    threshold: z.number().finite().optional(),
    render: z
        .discriminatedUnion('kind', [analyticsKpiSchema, resultChartSchema, resultTableSchema])
        .optional(),
    unavailable: z.string().min(1).optional(),
    /**
     * HOW THIS PREVIEW DIFFERS FROM THE WIDGET A SAVE WOULD KEEP — present
     * exactly when it differs (F308), and INDEPENDENT of `unavailable`.
     *
     * A preview is drawn by the read-only analytics engine; a saved dashboard
     * is drawn by the layout domain's own resolvers, whose axes are baked into
     * each bespoke query — one bar per documentation age band, one row per
     * named worker, a weekly trend of the last twenty-six weeks whatever period
     * the dashboard names. The engine has dimensions for almost none of those,
     * and it used to have a GUESS substituted for them: the same widget drew
     * one bar per program in the preview and twenty-six bars of weeks once
     * saved, with nothing anywhere saying so.
     *
     * So a widget that carries figures may also carry this, and a surface that
     * draws the figures must draw this beside them. It is the difference
     * between a preview that is a promise and one that is a picture.
     */
    savedDifference: z.string().min(1).optional(),
})
    .strict();
/**
 * The PROPOSED dashboard, DRAWN before anything is saved.
 *
 * Mirrors `planDashboardOutput` (`intelligence/tools/dashboardTools.ts`) — and
 * its `validatedSpec` is {@link dashboardSpecSchema}, already declared above
 * from `dashboards/spec.ts`.
 *
 * `widgetPreviews` is what makes this a preview rather than a description of
 * one. `dashboard.plan` executes each widget through the read-only analytics
 * engine under the caller's own scope, so the card carries the dashboard's
 * REAL figures. Nothing here saves, and — since a transient view of data the
 * caller may already read changes nothing — nothing here is confirmed either:
 * a preview turn issues no `pendingConfirmation` at all. Saving is a separate,
 * explicit act with its own confirmation, bound to the exact spec shown.
 */
export const resultDashboardPreviewSchema = z
    .object({
    kind: z.literal('dashboard_preview'),
    planId: z.string().uuid(),
    diffDigest: z.string().regex(SHA256_HEX),
    validatedSpec: dashboardSpecSchema,
    persistable: z.boolean(),
    reasons: z.array(z.string().min(1)),
    /** Absent when no analytics engine was wired — fail-closed, and visibly so. */
    widgetPreviews: z.array(resultDashboardWidgetPreviewSchema).max(6).optional(),
    /** The RESOLVED window the figures cover, from the executor's boundaries. */
    period: z.object({ from: z.string(), to: z.string() }).strict().optional(),
})
    .strict();
// --- The envelope -----------------------------------------------------------
/** One rendered object beside the answer, discriminated on `kind`. */
export const intelligenceResultSchema = z.discriminatedUnion('kind', [
    analyticsKpiSchema,
    resultTableSchema,
    resultChartSchema,
    resultRecordsSchema,
    resultAutomationSchema,
    resultDashboardPreviewSchema,
]);
// ---------------------------------------------------------------------------
// HTTP surface (apps/api/src/domains/intelligence/routes.ts — the two live
// doors). Added when the routes shipped; the withdrawal in the module header
// stands: nothing below touches recommendation persistence.
// ---------------------------------------------------------------------------
/** Lowercase-only sha256, matching the SERVER's request schema exactly: the
 * route's `turnBodySchema` refuses uppercase hex, so a request-side check that
 * tolerated it would pass locally and 400 on the wire. */
const SHA256_HEX_LOWER = /^[0-9a-f]{64}$/;
/** Bound on one user message (routes.ts `MAX_MESSAGE_CHARS`). */
export const MAX_TURN_MESSAGE_CHARS = 8_000;
/**
 * The `confirmation` field of a turn request (routes.ts `turnBodySchema`): the
 * two row-binding fields of a previously issued {@link ToolConfirmation},
 * presented back verbatim. The summary and clocks stay server-side — the
 * stored row, not this echo, is what gets consumed.
 */
export const turnConfirmationRequestSchema = z
    .object({
    proposalId: z.string().uuid(),
    planDigest: z.string().regex(SHA256_HEX_LOWER),
})
    .strict();
/**
 * POST /api/intelligence/turn body (routes.ts `turnBodySchema`, field for
 * field). `.strict()` matches the server's: an unknown key is a 400 there, so
 * it is a client-side parse error here — and in particular there is NO field
 * through which this client can supply conversation history; the server-held
 * session (resumed via `sessionId`) is the only history.
 */
export const turnRequestSchema = z
    .object({
    // Optional only alongside a confirmation (mirrors the server refine): a
    // confirm-only request restates nothing — the server supplies the
    // canonical instruction (F187).
    message: z.string().min(1).max(MAX_TURN_MESSAGE_CHARS).optional(),
    sessionId: z.string().uuid().optional(),
    /**
     * OPTIONAL durable thread to append this exchange to (routes.ts
     * `turnBodySchema.threadId`; the domain is `intelligenceThreads.ts`).
     *
     * ABSENT CHANGES NOTHING — the turn behaves exactly as it did before
     * durable threads existed and nothing is written, so a caller that never
     * sends this field cannot tell the feature shipped.
     *
     * NOT A SESSION ID, and not a replacement for one. `sessionId` keys the
     * in-memory transcript the MODEL sees; this keys the durable record of
     * what the PERSON saw. A surface that reopens a conversation sends BOTH:
     * the thread so the exchange is stored, the session so the model still has
     * its working history.
     *
     * OWNERSHIP IS NOT ASSERTED HERE. The server re-derives it live from the
     * authenticated principal and refuses a thread that is not the caller's
     * with a 404 BEFORE spending a model call — a colleague's thread and one
     * that never existed are the same answer. Sending a thread id is therefore
     * a request, never a claim.
     */
    threadId: z.string().uuid().optional(),
    /**
     * OPTIONAL recommendation this turn is acting on behalf of (routes.ts
     * `turnBodySchema.recommendationId`), which lights the §24 RECOMMENDED
     * ACTION and EXPECTED EFFECT elements of the answer.
     *
     * A REQUEST, NEVER A CLAIM, exactly like `threadId` above. Sending an id
     * asserts nothing: the server converts it into authority through the
     * recommendation domain's own gate — `recommendation:read` plus the stored
     * evidence manifest's reachability against the authenticated caller — and
     * an id that fails is DROPPED SILENTLY. The turn still runs and answers; it
     * simply carries no recommended action.
     *
     * SO A CLIENT CANNOT TELL WHETHER THE ID EXISTS. A record that is not
     * theirs, one that never existed, and a caller with no recommendation grant
     * produce the same response with the same status. Do not build a surface
     * that reads the absence of `explanation.recommendedAction` as "no such
     * recommendation" — it is not evidence of that, deliberately.
     */
    recommendationId: z.string().uuid().optional(),
    context: z.unknown().optional(),
    confirmation: turnConfirmationRequestSchema.optional(),
})
    .strict()
    .refine((body) => body.message !== undefined || body.confirmation !== undefined, {
    message: 'message is required unless a confirmation is being spent',
    path: ['message'],
});
/** The three grounding verdicts (grounding.ts `GroundingVerdict`). */
// 'uncited' (F186): the answer cites no evidence at all — a different fact
// from 'ungrounded', whose citations resolve to nothing. Small talk is
// uncited; fabrication is ungrounded; the UI renders them at different
// volumes on purpose.
export const groundingVerdictSchema = z.enum([
    'grounded',
    'partially_grounded',
    'ungrounded',
    'uncited',
]);
/** Why one declared citation failed (grounding.ts `CitationDefectCode`). */
export const citationDefectCodeSchema = z.enum([
    /** The ref had the old derived `toolId@n` shape — a model minted an id. */
    'fabricated_reference',
    /** Well-formed but never issued in this turn. */
    'unknown_reference',
    /** Issued, but nothing was actually read (denied or empty retrieval). */
    'unaccessed_evidence',
    /** An analytics entry that cannot carry every facet its number asserts. */
    'incomplete_analytical_evidence',
]);
/** One failed citation: the ref as written, plus why it failed. */
export const citationDefectSchema = z
    .object({
    ref: z.string(),
    code: citationDefectCodeSchema,
    /** For `incomplete_analytical_evidence`, the unsupported facets. */
    missingFacets: z.array(z.string()),
})
    .strict();
/**
 * The grounding audit that rides every turn response (grounding.ts
 * `GroundingAudit`, route obligation O5).
 *
 * The refs are SERVER-ISSUED evidence ids (`ev_` + 32 hex) — opaque
 * references the executor mints per tool call. They are deliberately not
 * derivable from a tool's name: an id the model could compute is an id the
 * model could invent, which is exactly how a fabricated citation once reached
 * a user.
 *
 * A UI renders the verdict honestly: `partially_grounded` and `ungrounded`
 * surface `notice` to the reader, and the answer text is never rewritten or
 * withheld — silently editing the model's words would hide exactly what the
 * audit exists to expose. `defects` is operator detail; it is not prose to
 * paste into a conversation.
 */
export const groundingAuditSchema = z
    .object({
    verdict: groundingVerdictSchema,
    /** Distinct declared refs supported by the manifest (accessed count > 0). */
    supported: z.array(z.string()),
    /** Distinct declared refs the manifest does not support (unknown id, an
     * entry whose accessed counts sum to zero, or incomplete analytical
     * evidence). */
    unsupported: z.array(z.string()),
    /** Distinct declared refs whose id never appears in the answer text. */
    orphanRefs: z.array(z.string()),
    /**
     * One entry per unsupported ref, saying why it failed.
     *
     * Optional on the wire (the server always sends it) so a consumer that
     * only renders the verdict — and every fixture written before this field
     * existed — stays valid.
     */
    defects: z.array(citationDefectSchema).optional(),
    /**
     * A short, honest sentence for the READER when verification failed.
     * Present only for `ungrounded` / `partially_grounded`. Names the
     * situation, never the machinery.
     */
    notice: z.string().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// The explanation envelope (§24 response contract; api `explain/composer.ts`)
// ---------------------------------------------------------------------------
/**
 * The closed mechanism vocabulary a factor may cite (`explain/types.ts`).
 *
 * A mechanism is what separates a cause from a coincidence, and the list is
 * closed for that reason: an open vocabulary would let "because" be asserted
 * by naming a new kind of because.
 */
export const explanationMechanismSchema = z.enum([
    'documented_rule',
    'threshold_crossing',
    'scheduled_delta',
    'process_failure',
    'intervention_effect',
]);
/** One factual statement plus the evidence ids it rests on. */
export const explanationStatementSchema = z
    .object({ text: z.string(), evidenceRefs: z.array(z.string()) })
    .strict();
/** One measured change: the metric and both endpoints, never one of them. */
export const explanationDeltaSchema = z
    .object({
    metric: z.string(),
    from: z.string(),
    to: z.string(),
    evidenceRef: z.string(),
})
    .strict();
/**
 * A likely factor, classified BY THE SERVER into one of three bases.
 *
 * `mechanism` is the only one that can support a causal claim; `correlation`
 * has evidence but no mechanism linking it to the change; `model_hypothesis`
 * has no evidence at all and says so rather than implying it. A surface that
 * renders all three identically has undone the distinction — the discriminant
 * is the point.
 */
export const explanationFactorSchema = z.discriminatedUnion('basis', [
    z
        .object({
        label: z.string(),
        statement: z.string(),
        basis: z.literal('mechanism'),
        evidenceRef: z.string(),
        mechanism: explanationMechanismSchema,
    })
        .strict(),
    z
        .object({
        label: z.string(),
        statement: z.string(),
        basis: z.literal('correlation'),
        evidenceRef: z.string(),
    })
        .strict(),
    z
        .object({ label: z.string(), statement: z.string(), basis: z.literal('model_hypothesis') })
        .strict(),
]);
/**
 * §24 DATA FRESHNESS — when each cited retrieval's data was last true.
 *
 * FIVE STATES, AND A SURFACE MUST NOT COLLAPSE THEM.
 *
 *   fresh / stale / expired — an as-of instant exists and was measured.
 *   unknown                 — no as-of instant. The source was never asked, or
 *                             was asked and could not say (`basis` tells you
 *                             which). It is NOT "now" and must never render as
 *                             current.
 *   not_applicable          — a catalog or definition read. Nothing here ages,
 *                             and putting a staleness note on it would be a
 *                             warning about a risk that does not exist.
 *
 * `composedAtMs` is when the ANSWER WAS COMPOSED. It is not freshness, and a
 * surface that labels it "as of" is telling the reader that a model ran.
 */
export const evidenceFreshnessStateSchema = z.enum([
    'fresh',
    'stale',
    'expired',
    'unknown',
    'not_applicable',
]);
/** Why a freshness state is what it is. A closed code, never a message. */
export const temporalBasisSchema = z.enum([
    /** The source declared when its data was last true; the state is measured. */
    'source_as_of',
    /** The source declares a class but could not report an instant. */
    'source_recency_unavailable',
    /** The retrieval declares no recency at all — never asked. */
    'source_undeclared',
    /** Nothing in this retrieval ages. */
    'source_not_time_varying',
]);
export const evidenceTemporalSchema = z
    .object({
    evidenceRef: z.string(),
    /** When the retrieval RAN. Not a freshness claim. */
    retrievedAtMs: z.number(),
    /** When the data was last true. Absent for unknown and not-applicable. */
    dataAsOfMs: z.number().optional(),
    ageMs: z.number().optional(),
    state: evidenceFreshnessStateSchema,
    basis: temporalBasisSchema,
    recency: z.enum(['live_first_party', 'mirrored', 'not_time_varying']).optional(),
})
    .strict();
/**
 * The one qualification a surface should put on the answer.
 *
 * `no_evidence` is a sixth answer and not a synonym for `not_applicable`: an
 * explanation that cited nothing time-bearing has no freshness to report, which
 * is a different statement from one whose every citation was a catalog.
 */
export const explanationTemporalSchema = z
    .object({
    composedAtMs: z.number(),
    evidence: z.array(evidenceTemporalSchema),
    qualification: z
        .object({
        state: z.union([evidenceFreshnessStateSchema, z.literal('no_evidence')]),
        drivenByRef: z.string().optional(),
    })
        .strict(),
})
    .strict();
/**
 * §24 RECOMMENDED NEXT ACTION + EXPECTED EFFECT.
 *
 * Read off the durable recommendation record, never written by the model.
 * ABSENT when no recommendation drove the answer — which is the ordinary case
 * and the honest one: the element appears only on a turn whose request named a
 * recommendation the server then authorized this caller to act on.
 *
 * `expectedEffect` is separately optional. `recommendation_option.expected_effect`
 * landed in migration `1809000000000` and is mandatory on the recommended
 * option, so a record written since always supplies one; a record written
 * before it has a NULL. A surface must render that absence as "not stated"
 * rather than inventing one from the action text.
 */
export const explanationRecommendedActionSchema = z
    .object({
    recommendationId: z.string(),
    optionId: z.string(),
    action: z.string(),
    toolName: z.string().nullable(),
    risk: z.string(),
    confirmationRequired: z.boolean(),
    advisoryOnly: z.boolean(),
    expectedEffect: z.string().optional(),
})
    .strict();
/**
 * §24 OWNER — who is accountable for the thing the answer is about.
 *
 * WITHHELD IS NOT ABSENT, and a surface must not render them the same way. The
 * whole element missing means nothing canonical names an owner (no single
 * program in scope, or a program with no lead). The element PRESENT with
 * `identityWithheld: true` means there IS an owner and this viewer may not be
 * told who — "not shown to you", never "unowned".
 *
 * `dated: false` says the naming relation is undated: this is who owns the
 * program NOW, not who owned it when the data was true.
 */
export const explanationOwnerSchema = z
    .object({
    source: z.literal('program_lead'),
    programId: z.string(),
    workerId: z.string().nullable(),
    displayName: z.string().nullable(),
    identityWithheld: z.boolean(),
    dated: z.boolean(),
})
    .strict();
/**
 * WHERE A RULE CAME FROM, as much of it as this reader may be told.
 *
 * `null` on the element means WITHHELD, and the element's `sourceWithheld` flag
 * says so — notation (B) of `docs/withholding-contract.md`. A surface that
 * rendered a withheld source as "unsourced" would send a compliance reviewer to
 * fix a corpus that is not broken.
 */
export const policySourceSchema = z
    .object({
    sourceId: z.string(),
    title: z.string(),
    sourceClass: z.string(),
    publisher: z.string().optional(),
    citation: z.string().optional(),
    referenceUrl: z.string().optional(),
    jurisdiction: z.string().optional(),
    /** `v3` — the version of the approved RULE, not of the document. */
    version: z.string().optional(),
})
    .strict();
/** Who made this rule authoritative in this organization, and when. */
export const policyApprovalSchema = z
    .object({ approvedByUserId: z.string(), approvedAt: z.string() })
    .strict();
/**
 * One applicable rule, with everything a reader needs to CHECK it.
 *
 * `role` is carried rather than flattened into "applies": `external_floor` and
 * `advisory` must not read the same in an output somebody will act on — "the
 * statute requires" and "the manual suggests" are different claims — and
 * `superseded_by_more_specific` is the answer to "which rule lost, and to what".
 */
export const policySourceElementSchema = z
    .object({
    ruleId: z.string(),
    subject: z.string(),
    /** The rule in plain language — the approved statement, verbatim. */
    /**
     * THE RULE TEXT, AS AN UNTRUSTED EXCERPT RATHER THAN A STRING.
     *
     * This mirrors `UntrustedExcerpt` in
     * `apps/api/src/domains/intelligence/untrustedExcerpt.ts`. It was
     * `z.string()` until 2026-09-07, and the drift was the SECOND turn-deleting
     * one in this schema in a day: the server began sending an object, this
     * object is `.strict()`, and `turn()` calls `.parse` rather than
     * `.safeParse` — so every answer that resolved a doctrine rule threw here
     * after the server had already done the work, taking a completed turn with
     * it. `asOf` had done the same thing one field over.
     *
     * The shape is mirrored in full rather than as `z.unknown()`, because the
     * point of a strict schema is that the client notices when the server
     * changes. Loosening it to stop the throw would keep the symptom away and
     * throw away the detector.
     */
    rule: z
        .object({
        /** One value. A reader asking how far this may influence anything finds it here. */
        trust: z.literal('untrusted_reference_data'),
        /** PHI-masked, framing-neutralized, capped. Never raw. */
        text: z.string(),
        /** Content-derived fence. Present on every excerpt, never only the dangerous ones. */
        fence: z.string(),
        /** True when the cap bit; false means the whole rule is here. */
        excerpted: z.boolean(),
        /** True when a PHI value shape was masked out. */
        redacted: z.boolean(),
        /** A REVIEWER HINT, never a gate — delivery is identical either way. */
        instructionShaped: z.boolean(),
        /** Which shapes matched, by name. Empty when none did. */
        instructionSignals: z.array(z.string()),
    })
        .strict(),
    requirement: z
        .object({
        kind: z.string(),
        value: z.number().optional(),
        unit: z.string().optional(),
    })
        .strict(),
    authority: z.string(),
    bindingness: z.string(),
    ruleLevel: z.string(),
    role: z.enum([
        'governing',
        'external_floor',
        'advisory',
        'superseded_by_more_specific',
        'doctrine_default',
    ]),
    source: policySourceSchema.nullable(),
    /** ALWAYS PRESENT. True when the caller may not read the source document. */
    sourceWithheld: z.boolean(),
    /** `null` when the material has no approver at all (the external library). */
    approval: policyApprovalSchema.nullable(),
    /** ALWAYS PRESENT. True when the approver is withheld rather than absent. */
    approvalWithheld: z.boolean(),
    effectiveFrom: z.string().nullable(),
    effectiveTo: z.string().nullable(),
})
    .strict();
/**
 * The §24 APPLICABLE POLICY / SOURCE element — canonical, never model-authored.
 *
 * `subjects` are CLOSED-VOCABULARY KEYS resolved by the server against the
 * organization's own corpus, never the free text a person typed.
 */
export const applicablePolicySchema = z
    .object({
    elements: z.array(policySourceElementSchema),
    /**
     * THE CALENDAR DATE THE RESOLUTION WAS NARROWED BY (UTC), or `null` when
     * the context named a moment that is not a date.
     *
     * B7e composes it in `explain/policySource.ts` off the resolution's OWN
     * context, through the same `asOfDate` the repository queries used — so if
     * it says `2026-06-01`, `selectActiveApproved` was given `2026-06-01`.
     *
     * IT WAS MISSING FROM THIS SCHEMA AND THE OBJECT IS `.strict()`, so every
     * turn whose explanation carried an applicable-policy element failed
     * `turnResponseSchema.parse` with `Unrecognized key(s): 'asOf'` — and
     * `turn()` parses with `.parse`, not `.safeParse`, so the THROW discarded a
     * whole answer that had already run, side effects included. A field this
     * file did not know about did not degrade the render; it deleted the turn.
     *
     * REQUIRED AND NULLABLE, mirroring `ApplicablePolicy.asOf`'s
     * `string | null` field-for-field, which is this module's stated
     * drift-loudly discipline. `null` is not a missing field: it is
     * `invalid_context_effective_date` territory — every candidate was
     * rejected for it, so `elements` is empty and the null EXPLAINS the
     * emptiness. A surface must not render it as "no policy applies".
     */
    asOf: z.string().nullable(),
    subjects: z.array(z.string()),
    /**
     * True when an applicable internal policy would have been MORE PERMISSIVE
     * than an external hard requirement and was clamped to it. The single fact
     * a compliance reviewer is looking for.
     */
    constrainedByExternal: z.boolean(),
    /**
     * Contradictions precedence could not settle — two applicable rules of
     * equal standing pulling opposite ways. Surfaced rather than decided,
     * because quietly choosing would be the system settling something a human
     * has not.
     */
    unresolvedContradictions: z.array(z.object({ subject: z.string(), between: z.tuple([z.string(), z.string()]) }).strict()),
    /**
     * Internal policy that CONTRADICTS an applicable absolute requirement
     * rather than merely being clamped by it. Reportable: it means the
     * organization's written policy conflicts with a rule they cannot opt out
     * of, and somebody should be told.
     */
    reportableConflicts: z.array(z
        .object({
        subject: z.string(),
        policyRuleId: z.string(),
        displacedByRuleId: z.string(),
    })
        .strict()),
})
    .strict();
/**
 * The composed, validated explanation. Produced only by the server's composer:
 * every evidence id in it resolved against the turn's sealed manifest, and the
 * organization on `affectedScope` is the server's own, never the model's.
 */
export const explanationSchema = z
    .object({
    whatHappened: explanationStatementSchema,
    whatChanged: z.array(explanationDeltaSchema),
    whyItMatters: z.string(),
    supportingEvidence: z.array(z.string()),
    affectedScope: z
        .object({
        orgId: z.string(),
        programIds: z.array(z.string()),
        siteIds: z.array(z.string()),
    })
        .strict(),
    uncertainty: z
        .object({ level: z.enum(['none', 'low', 'medium', 'high']), basis: z.string() })
        .strict(),
    assertsCausation: z.boolean(),
    likelyFactors: z.array(explanationFactorSchema),
    /** §24 data freshness. Absent only when the server had no clock. */
    temporal: explanationTemporalSchema.optional(),
    /** §24 recommended next action + expected effect. Absent, never invented. */
    recommendedAction: explanationRecommendedActionSchema.optional(),
    /** §24 owner. Absent when nothing canonical names one. */
    owner: explanationOwnerSchema.optional(),
    /**
     * §24 APPLICABLE POLICY / SOURCE.
     *
     * ABSENT means no doctrine was resolved this turn. An `ApplicablePolicy`
     * whose `elements` are empty is a different answer: the corpus WAS consulted
     * on a subject it covers and nothing applied. Collapsing the two would put
     * "no policy governs you" in front of a reader for whom nothing was ever
     * looked up. Optional rather than nullable, matching the three §24 elements
     * above — one file, one spelling of absent.
     */
    applicablePolicy: applicablePolicySchema.optional(),
})
    .strict();
/** Why the composer refused an envelope. A closed vocabulary, never a message. */
export const explanationRejectionCodeSchema = z.enum([
    /** Structurally not an explanation (missing or badly shaped fields). */
    'invalid_envelope',
    /** A declared evidence id does not resolve to a real, accessed retrieval. */
    'unsupported_evidence_ref',
    /** Causation asserted over factors that carry no typed mechanism link. */
    'causation_without_mechanism',
]);
/**
 * The turn's explanation verdict.
 *
 * THREE STATES, AND A SURFACE MUST NOT COLLAPSE THEM. The field ABSENT means
 * the answer carried no explanation — the ordinary case, and the honest one for
 * a figure or a list. `{ok:false}` means the model wrote an explanation and the
 * server REFUSED it; rendering that as "no explanation" hides a failed check,
 * and rendering the refused content is impossible on purpose — it is not on the
 * wire. `{ok:true}` is an explanation every claim of which is pinned to a
 * retrieval this turn actually made.
 */
export const explanationVerdictSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), explanation: explanationSchema }).strict(),
    z.object({ ok: z.literal(false), code: explanationRejectionCodeSchema }).strict(),
]);
/**
 * POST /api/intelligence/turn response (routes.ts, the final `reply.send`):
 * `summary` is the existing {@link turnSummarySchema}; `evidence` is the
 * turn's whole manifest ({@link toolEvidenceSchema} per entry, failures
 * included); `pendingConfirmation`, when present, is the server-issued,
 * row-anchored {@link toolConfirmationSchema} the caller presents back as a
 * {@link turnConfirmationRequestSchema}.
 */
// ---------------------------------------------------------------------------
// Action proposals (lane B-ACTIONS)
// ---------------------------------------------------------------------------
/**
 * THE CLOSED ACTION VOCABULARY, mirrored. The server's
 * `intelligence/actions/catalog.ts` is the authority; this enum exists so the
 * surface can switch on a member rather than on a string, and so a member the
 * client does not know fails the parse loudly instead of rendering as a button
 * whose behaviour nobody has thought about.
 */
export const EXECUTABLE_ACTION_TYPES = [
    'messaging.notify_program',
    'tasks.create_followup',
    'tasks.flag_for_review',
];
/** The three closed values an action carries. No free-text field exists. */
export const actionParamsSchema = z
    .object({
    subjectId: z.string().uuid(),
    subjectToken: z.enum([
        'shift',
        'time_session',
        'credential',
        'supervision',
        'worker',
        'client',
    ]),
    quantity: z.number().int().min(0).max(9999),
})
    .strict();
/**
 * An action the answer offers. `label` is SERVER-AUTHORED from a template
 * table — no part of it was composed by the model — so a surface may render it
 * as-is. Note what is deliberately NOT here: the permission the action needs,
 * the scope it is evaluated at, and its risk class. The server re-derives all
 * three at execution from `actionType` alone, and publishing them would invite
 * a surface to make an authorization decision out of them. Enablement already
 * happened: an action that appears here is one this caller was offered.
 */
export const offeredActionSchema = z
    .object({
    actionType: z.enum(EXECUTABLE_ACTION_TYPES),
    label: z.string().min(1).max(160),
    targetDomain: z.string().min(1).max(48),
    kind: z.string().min(1).max(24),
    params: actionParamsSchema,
    confirmation: toolConfirmationSchema,
    /** True when the person must choose a channel before this can execute. */
    requiresChannel: z.boolean(),
})
    .strict();
/**
 * A proposed action this caller may NOT take, reported WITHOUT its subject.
 *
 * Every value below is a closed-enum token. There is no entity id, no name, no
 * permission key and no scope id — by construction, not by redaction: the
 * server's `degradation.ts` response type has no field one could travel in. A
 * greyed-out button carrying its subject would be an existence oracle with a
 * disabled attribute; this is a statement that a refusal happened, in a
 * vocabulary that names nothing.
 */
export const withheldActionSchema = z
    .object({
    refusalKind: z.enum(['permission_denied', 'scope_denied', 'not_found_style']),
    explanation: z
        .object({
        template: z.string().min(1).max(64),
        tokens: z
            .object({
            refusalKind: z.string().min(1).max(32),
            targetDomain: z.string().min(1).max(48),
            scopeType: z.string().min(1).max(24),
        })
            .strict(),
    })
        .strict(),
    alternatives: z
        .object({
        keepInConversation: z.boolean(),
        personalViewIfPermitted: z.boolean(),
        escalateToAdmin: z.boolean(),
    })
        .strict(),
    noSilentNarrowing: z.literal(true),
})
    .strict();
/** Where an executed action landed. Two EXISTING hubs; there is no seventh. */
export const actionLandingSchema = z
    .object({
    hub: z.enum(['messages', 'operations']),
    recordKind: z.enum(['message', 'task']),
    recordId: z.string().uuid(),
    channelId: z.string().uuid().optional(),
})
    .strict();
export const executeActionRequestSchema = z
    .object({
    actionType: z.enum(EXECUTABLE_ACTION_TYPES),
    params: actionParamsSchema,
    channelId: z.string().uuid().optional(),
})
    .strict();
export const executeActionResponseSchema = z
    .object({
    executed: z.literal(true),
    landing: actionLandingSchema,
    requestId: z.string(),
})
    .strict();
export const turnResponseSchema = z
    .object({
    turnId: z.string().uuid(),
    sessionId: z.string().uuid(),
    answer: z.string(),
    summary: turnSummarySchema,
    evidence: z.array(toolEvidenceSchema),
    grounding: groundingAuditSchema,
    /**
     * SERVED (B7a — `intelligence/explain/composer.ts`).
     *
     * The §24 explanation envelope, present ONLY when the answer carried one.
     * See {@link explanationVerdictSchema}: absent, refused and composed are
     * three different facts and the field keeps them different.
     */
    explanation: explanationVerdictSchema.optional(),
    pendingConfirmation: toolConfirmationSchema.optional(),
    /**
     * SERVED (F221a — `intelligence/results/project.ts`).
     *
     * It carries the typed objects an answer is ABOUT
     * ({@link intelligenceResultSchema}: KPI, table, chart, record cards,
     * automation, dashboard preview) so the surface can RENDER them instead of
     * mining the prose for numbers. Every element was projected
     * from a tool outcome the turn's evidence manifest records — never from the
     * answer text, which the projector is not even given — and a refused or
     * failed tool contributes none. Still optional on a `.strict()` object: a
     * turn that produced no typed object omits the field, and absent → the
     * surface renders nothing, which is the correct amount of nothing.
     */
    results: z.array(intelligenceResultSchema).max(20).optional(),
    /**
     * Whether this exchange reached the durable conversation the request
     * named — present ONLY when the request carried a `threadId`, so a turn
     * that asked for no persistence serves the bytes it always did.
     *
     * `false` IS A REAL ANSWER, not an error. The turn RAN: its answer is in
     * this response and its side effects (audit rows, a created dashboard, a
     * consumed confirmation) are already durable. Only the save to the
     * transcript failed, and the server reports that rather than answering
     * 500 — which would describe a failure that did not happen and invite a
     * retry that would run those side effects a second time.
     *
     * So a surface must NOT retry the turn on `false`. It should render the
     * answer normally and tell the person this one was not saved to the
     * conversation; silently dropping it would leave somebody believing a
     * conversation was recorded that was not.
     */
    threadAppended: z.boolean().optional(),
    /**
     * SERVED (lane B-ACTIONS — `intelligence/actions/`).
     *
     * The actions this answer offers. Derived on the server from the turn's
     * SEALED evidence manifest — never from the answer text, which the
     * deriving module is not given — so every subject named here came out of a
     * retrieval this caller was already authorized for.
     *
     * OFFERING IS NOT AUTHORIZING. The server re-authorizes at execution
     * against the acting person's freshly-resolved context and consults
     * nothing that produced this array. A surface must therefore never treat
     * the presence of an entry as permission, and must handle a 403 on
     * execute as an ordinary outcome rather than as an impossible one.
     *
     * Omitted when a turn offers none, so an answer with nothing to act on
     * serves the bytes it always did.
     */
    actionProposals: z.array(offeredActionSchema).max(8).optional(),
    /**
     * SERVED (lane B-ACTIONS — `intelligence/actions/degradation.ts`).
     *
     * Actions this answer could have offered and did not, reported in closed
     * tokens that name no entity. See {@link withheldActionSchema}.
     */
    withheldActions: z.array(withheldActionSchema).max(8).optional(),
    requestId: z.string(),
})
    .strict();
// ---------------------------------------------------------------------------
// The progress channel (F221b)
// ---------------------------------------------------------------------------
/**
 * What a turn reports about ITSELF while it is still running.
 *
 * A turn takes eight to fifteen seconds against real data. Until this existed
 * the caller had one event — the finished response — and a person watched an
 * indeterminate spinner for the whole of it.
 *
 * THE ANSWER IS NOT IN HERE, AND THAT IS THE DESIGN, NOT A GAP. The server
 * audits the finished answer for grounding against a SEALED evidence manifest
 * and only then strips the citation tokens out of it; both steps need the
 * whole text, and the redaction needs it because a citation arrives over the
 * wire in fragments. Streaming prose would mean serving text that had not been
 * audited yet, or text whose evidence ids had not been cut out yet. Neither is
 * worth a smoother animation, so prose arrives once, whole, audited and
 * redacted — see `apps/api/src/domains/intelligence/progress.ts` for the long
 * form of the argument.
 *
 * WHAT IS IN HERE IS ALREADY IN THE FINAL RESPONSE. Every field of a
 * `progress` frame is published one moment later by `summary.toolOutcomes`
 * (`toolId`, `outcome` — this file's own {@link turnToolOutcomeSchema}, reused
 * below rather than restated so the two cannot drift). A streaming client
 * learns nothing a blocking client would not have learned; it learns it
 * sooner. That is the entire privacy argument for the channel and it is what
 * makes it safe to render.
 */
export const turnPhaseSchema = z.enum(['retrieving', 'computing', 'preparing', 'working']);
export const turnProgressFrameSchema = z.discriminatedUnion('kind', [
    /** The server has the turn. Emitted once, before any model call. */
    z.object({ kind: z.literal('accepted'), sessionId: z.string().uuid() }).strict(),
    /**
     * One COMPLETED tool round. Completed, not started: a starting frame would
     * name a tool that authorization may be about to refuse, which tells a
     * person something untrue about their own turn in the direction that implies
     * access. `index` is 1-based and monotonic within the turn.
     */
    z
        .object({
        kind: z.literal('progress'),
        index: z.number().int().min(1),
        phase: turnPhaseSchema,
        toolId: turnToolOutcomeSchema.shape.toolId,
        outcome: turnToolOutcomeSchema.shape.outcome,
    })
        .strict(),
]);
/**
 * A phase, as a sentence a person would accept as true about their own turn.
 *
 * Present-continuous and about THE WORK, never about the machine: "Checking
 * your records" rather than "invoking workforce.list_workers@2". The runtime
 * vocabulary firewall in the admin surface exists to keep wire identifiers off
 * a screen, and a progress line is not an exemption from it.
 *
 * `working` is the honest fallback for a tool family the phase map has not
 * been taught about — it says a turn is running and claims nothing else,
 * which is exactly what is known.
 */
export function phaseNote(phase) {
    switch (phase) {
        case 'retrieving':
            return 'Checking your records…';
        case 'computing':
            return 'Working out the figures…';
        case 'preparing':
            return 'Preparing the change…';
        case 'working':
            return 'Working on your question…';
    }
}
/**
 * One tool as GET /api/intelligence/tools advertises it (turn.ts
 * `advertiseToolSpecs` → `{ name, description, inputSchema }`). Deliberately
 * NOT {@link toolAdvertisementSchema}: that shape mirrors the domain's
 * internal `ToolDefinition`; the route serves this narrower projection, with
 * permission groups and risk classes staying server-side.
 *
 * DECIDED (F185): both schemas stay, on purpose. The narrow one is the wire —
 * a caller learns what Phibian can do for them, not how the doors are gated;
 * advertising permission groups would hand an enumeration of the authorization
 * surface to whoever holds intelligence:use. The rich one is the runtime's
 * internal contract. If the route ever grows the projection, it grows THIS
 * schema deliberately — the other is not a fallback.
 */
export const advertisedToolSchema = z
    .object({
    name: z.string().min(1),
    description: z.string().min(1),
    inputSchema: jsonSchemaObjectSchema,
})
    .strict();
/** GET /api/intelligence/tools response envelope. */
export const toolsResponseSchema = z
    .object({
    tools: z.array(advertisedToolSchema),
    requestId: z.string(),
})
    .strict();
/**
 * `POST /api/intelligence/turn` — one assistant turn.
 *
 * Needs `intelligence:use`. The request is parsed BEFORE the wire, so a
 * malformed body fails here attributably instead of as an anonymous server
 * 400; the response is parsed so contract drift fails loudly instead of
 * rendering wrong. Distinct non-2xx answers a screen must branch on:
 *   503 `intelligence_provider_unconfigured` — no provider in this deployment
 *       (an honest not-configured state, not a retryable failure);
 *   409 `confirmation_not_live` — the presented confirmation was already
 *       used, expired, was issued to someone else, or names a different plan;
 *   403 — `intelligence:use` is not held (the withheld state).
 */
export function turn(body, options) {
    const parsed = turnRequestSchema.parse(body);
    const onProgress = options?.onProgress;
    if (onProgress === undefined) {
        return post('/api/intelligence/turn', parsed, options).then((response) => turnResponseSchema.parse(response));
    }
    return postEventStream('/api/intelligence/turn', parsed, (frame) => {
        // PARSED, LIKE EVERYTHING ELSE ON THIS WIRE, and dropped rather than
        // thrown on if it does not fit. A malformed progress frame is a cosmetic
        // problem; a turn that already ran is not, and must not be lost to one.
        const decoded = turnProgressFrameSchema.safeParse(frame);
        if (decoded.success)
            onProgress(decoded.data);
    }, options).then((response) => turnResponseSchema.parse(response));
}
/**
 * `GET /api/intelligence/tools` — what the assistant can do for THIS caller.
 *
 * Needs `intelligence:use`. The list resolves from the caller's real
 * permissions and is a hint only — the executor re-checks authorization before
 * anything runs — so a UI may render it as capability copy, never as a grant.
 */
export function tools(options) {
    return get('/api/intelligence/tools', options).then((response) => toolsResponseSchema.parse(response));
}
/**
 * `POST /api/intelligence/actions/execute` — take one action the answer offered.
 *
 * Needs `intelligence:use` TO REACH THE ROUTE and the action's own permission
 * TO DO ANYTHING — `messaging:send` at the program's scope, or `task:write`
 * org-wide. The two are different questions and the route answers them
 * separately: a caller with `intelligence:use` alone gets a 403 here.
 *
 * The request is parsed before the wire, so a body outside the closed
 * vocabulary fails attributably rather than as an anonymous 400. Non-2xx
 * answers a screen must branch on:
 *   400 `invalid_action_request` — not a representable action;
 *   403 `action_not_authorized` — carries a `degradation` object (closed
 *       tokens only) explaining that it was refused, naming nothing;
 *   409 `action_confirmation_not_live` — already taken, expired, or offered to
 *       someone else. The remedy is to ask again, never to retry this call;
 *   409 `action_not_completed` — the owning product declined the write.
 */
export function executeAction(body, options) {
    const parsed = executeActionRequestSchema.parse(body);
    return post('/api/intelligence/actions/execute', parsed, options).then((response) => executeActionResponseSchema.parse(response));
}
//# sourceMappingURL=intelligence.js.map